import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Paper,
} from '@material-ui/core';
import { Alert as AlertIcon, Warning as WarningIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';

import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 600,
      width: '100%',
    },
  },
  previewBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  orderInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
}));

const statusMessages = {
  PAYMENT_APPROVED: {
    title: "✅ Pagamento Aprovado!",
    message: "Seu pedido #{code} foi confirmado e está sendo preparado.",
    emoji: "✅"
  },
  CONFIRMED: {
    title: "👍 Pedido Confirmado!",
    message: "Seu pedido #{code} foi confirmado e está sendo processado.",
    emoji: "👍"
  },
  IN_PREPARATION: {
    title: "👨‍🍳 Em Preparação",
    message: "Seu pedido #{code} está sendo preparado com carinho.",
    emoji: "👨‍🍳"
  },
  READY_TO_PICKUP: {
    title: "📦 Pronto para Retirada",
    message: "Seu pedido #{code} está pronto! Pode vir buscar.",
    emoji: "📦"
  },
  SHIPMENT: {
    title: "🚚 Saiu para Entrega",
    message: "Seu pedido #{code} saiu para entrega!\nChegará em breve no endereço cadastrado.",
    emoji: "🚚"
  },
  DELIVERED: {
    title: "🎉 Entregue!",
    message: "Seu pedido #{code} foi entregue com sucesso!\nEsperamos que goste. Avalie sua experiência!",
    emoji: "🎉"
  },
  CANCELED: {
    title: "❌ Pedido Cancelado",
    message: "Seu pedido #{code} foi cancelado.\nSe tiver dúvidas, entre em contato conosco.",
    emoji: "❌"
  },
  RETURNED: {
    title: "🔄 Pedido Devolvido",
    message: "Seu pedido #{code} foi devolvido.\nEntraremos em contato para mais informações.",
    emoji: "🔄"
  }
};

const OrderNotificationModal = ({ open, order, onClose }) => {
  const classes = useStyles();
  const [selectedStatus, setSelectedStatus] = useState(order?.status || '');
  const [customMessage, setCustomMessage] = useState('');
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!order?.customerPhone) {
      toast.error('Pedido não possui telefone para envio');
      return;
    }

    try {
      setSending(true);

      const payload = {
        phone: order.customerPhone,
        status: selectedStatus,
      };

      if (useCustomMessage && customMessage.trim()) {
        payload.customMessage = customMessage.trim();
      }

      await api.post('/orders/test-notification', payload);
      toast.success('Notificação enviada com sucesso!');
      onClose();
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      toast.error('Erro ao enviar notificação: ' + (error.response?.data?.message || error.message));
    } finally {
      setSending(false);
    }
  };

  const getPreviewMessage = () => {
    if (useCustomMessage && customMessage.trim()) {
      return customMessage.replace('{code}', order?.code || '');
    }

    const template = statusMessages[selectedStatus];
    if (!template) return '';

    const message = `${template.title}\n\n${template.message}`;
    return message.replace('{code}', order?.code || '');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!order) return null;

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle>
        Enviar Notificação WhatsApp
      </DialogTitle>

      <DialogContent>
        {/* Order Info */}
        <Box className={classes.orderInfo}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Pedido #{order.code}
          </Typography>
          <Typography variant="body2">
            Cliente: {order.customerName}
          </Typography>
          <Typography variant="body2">
            Telefone: {order.customerPhone || 'Não informado'}
          </Typography>
          <Typography variant="body2">
            Total: {formatCurrency(order.totalAmount)}
          </Typography>
          <Box mt={1}>
            <Chip
              label={order.status}
              color="primary"
              size="small"
            />
          </Box>
        </Box>

        {!order.customerPhone && (
          <Paper
            style={{
              padding: 16,
              marginBottom: 16,
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <WarningIcon style={{ color: '#856404' }} />
            <Typography variant="body2" style={{ color: '#856404' }}>
              Este pedido não possui telefone cadastrado para envio de notificação.
            </Typography>
          </Paper>
        )}

        {/* Status Selection */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Status para Notificação</InputLabel>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            {Object.entries(statusMessages).map(([status, config]) => (
              <MenuItem key={status} value={status}>
                {config.emoji} {config.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Message Option */}
        <Box mt={2}>
          <FormControl component="fieldset">
            <Box display="flex" gap={1}>
              <Button
                variant={!useCustomMessage ? "contained" : "outlined"}
                color="primary"
                onClick={() => setUseCustomMessage(false)}
                size="small"
              >
                Mensagem Padrão
              </Button>
              <Button
                variant={useCustomMessage ? "contained" : "outlined"}
                color="primary"
                onClick={() => setUseCustomMessage(true)}
                size="small"
              >
                Mensagem Personalizada
              </Button>
            </Box>
          </FormControl>
        </Box>

        {/* Custom Message Input */}
        {useCustomMessage && (
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Mensagem Personalizada"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            margin="normal"
            helperText="Use {code} para inserir o código do pedido na mensagem"
          />
        )}

        {/* Message Preview */}
        {selectedStatus && (
          <Box className={classes.previewBox}>
            <Typography variant="subtitle2" gutterBottom>
              Pré-visualização da Mensagem:
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                margin: 0,
              }}
            >
              {getPreviewMessage()}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          variant="contained"
          disabled={!selectedStatus || !order.customerPhone || sending}
        >
          {sending ? 'Enviando...' : 'Enviar Notificação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderNotificationModal;